'use client';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormFieldLayout, InputField, Button } from '@AydiTeam1/aydi-ui';
import { useCheckAuth, useJoinAccountByInvitation, useLogin } from '../api';
import Link from 'next/link';
import { routes, appRoutes, authorizedRoutes } from '@/modules/shared/routes';

import { useAppRouter } from '@/modules/shared/hooks/useAppRouter';
import {  useTranslations } from 'next-intl';
import { useAnalytics } from '@/modules/shared/hooks/useAnalytics';
import { useEffect, useState } from 'react';
import SocialLoginContainer from './SocialLoginContainer';
import { InvitationParams, SignInFormSchema } from '../types';
import {  useSearchParams } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { useDetectLinkRedirect } from '../hooks/useDetectLinkRedirect';

const Login = ({ id, uuid }: Partial<InvitationParams>) => {
  const t = useTranslations('authentication');
  const { page, track } = useAnalytics();
  const searchParams = useSearchParams();
  const email = decodeURIComponent(searchParams.get('email') || '');
  const isInvitation = !!(uuid && id);
  
  const { navigate } = useAppRouter([
    '/orth',
    routes.verifyEmail,
    routes.signup,
    routes.invitationSignup,
    routes.verifyInvitationEmail,
  ]);
  const redirectUrl = searchParams.get('redirect');
  const emailParam = searchParams.get('email');
  const redirectedToPayment = searchParams.get('redirected-to-payment') === 'true';
  const queryClient = useQueryClient();
  
  useDetectLinkRedirect();

  useEffect(() => {
    if(!isInvitation) {
      page('register_email_screen');
    }else {
      page("register_email_screen_from_invetion")
    }
    // used in subscription
    // Social login leaves the app entirely, so the invitation context has to
    // survive the round trip — including the invited email, which is the only
    // way back to this exact invitation if the provider hand-off fails.
    if (isInvitation)
      sessionStorage.setItem(
        'invitation',
        JSON.stringify({
          accountId: id,
          uuid,
          email: emailParam ?? undefined,
        })
      );
    // Reaching the plain login page — via the logo, say — means the user left
    // the invitation. Keeping the context would make SocialCallback post to the
    // invitation endpoint with a different email and fail validation, which
    // bails back into the invitation flow.
    else sessionStorage.removeItem('invitation');
    // Store redirected-to-payment flag for social login flow
    if (redirectedToPayment) {
      sessionStorage.setItem('redirected-to-payment', 'true');
    }
  }, [redirectedToPayment, isInvitation, redirectUrl, emailParam, id, uuid]);

  const signInSchema = z
    .object({
      email: z.email(t('emailNotValid')).min(1, t('emailRequired')),
      password: z.string().optional(),
      passwordVisible: z.boolean(),
    })
    .refine(
      ({ password, passwordVisible }) =>
        !passwordVisible || (passwordVisible && password),
      {
        error: t('passwordRequired'),
        path: ['password'],
      }
    );

  const form = useForm<SignInFormSchema>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: emailParam || '',
      password: '',
      passwordVisible: false,
    },
  });

  // Update email field when emailParam changes
  useEffect(() => {
    if (emailParam) {
      form.setValue('email', emailParam);
    }
  }, [emailParam, form]);
  const passwordVisible = form.watch('passwordVisible');

  const { mutate: login, isPending: isLoginPending } = useLogin();
  const { mutate: checkAuth, isPending: isCheckAuthPending } = useCheckAuth();
  const { mutate: joinAccount, isPending: isJoinAccountPending } =
    useJoinAccountByInvitation();
  // we are using this to keep the loader after the submit ends
  const [isContinueLoading, setIsContinueLoading] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  const buildInvitationRoute = (route: string, email: string) =>
    `${route
      .replace('[id]', id!)
      .replace('[uuid]', uuid!)}?email=${encodeURIComponent(email)}`;

  const stopLoading = () => {
    setIsContinueLoading(false);
    setIsLoginLoading(false);
  };

  const handleCheckAuth = (email: string) => {
    setIsContinueLoading(true);
    checkAuth(
      { email },
      {
        onSuccess: ({ exists }) => {
            // Track with different events for invitation vs normal flow
          if (isInvitation) {
            track('continue_after_entering_email_from_invetion_link', {
              email,
              account_id: id,
              isNew: exists ? 0 : 1,
            });
          } else {
            track('continue_after_entering_email', { 
              email, 
              isNew: exists ? 0 : 1 
            });
          }

          if (exists) {
            form.setValue('passwordVisible', true);
          } else {
            const route = isInvitation
              ? buildInvitationRoute(routes.invitationSignup, email)
              : `${routes.signup}?email=${encodeURIComponent(email)}`;
            navigate(route, 'replace');
          }
        },
        onError: stopLoading,
      }
    );
  };

  const handleLogin = (data: SignInFormSchema) => {
    setIsLoginLoading(true);
    login(
      {
        email: data.email,
        password: data.password as string,
        ...(isInvitation && { accountId: id, uuid, isInvitedUser: true }),
      },
      {
        onSuccess: ({ status }) => {
          localStorage.setItem('showWelcome', 'true');
          // Invalidate userProfile query after successful login
          // This is needed in case of delete account and login again
          queryClient?.invalidateQueries({ queryKey: ['userProfile'] });
          
          if (status === 'UNVERIFIED') {
            const route = isInvitation
            ? buildInvitationRoute(routes.verifyInvitationEmail, data.email)
            : routes.verifyEmail;
            navigate(route, 'replace');
            return;
          }
          
          if (isInvitation) {
            track('continue_after_checking_invitation_validated', {
              email: data.email,
              isNew: 0,
            });
            joinAccount(
              { accountId: Number(id), uuid: uuid!, email: data.email },
              { onSuccess: () => navigate(appRoutes.orth, 'replace') }
            );
            return;
          }
          track('user_clicks_on_login', { email: data.email });

          const finalRedirectUrl = redirectedToPayment
            ? authorizedRoutes.teamSubscription
            : redirectUrl?.includes('mobileSubscribe')
            ? `${appRoutes.subscription}?from=mobileSubscribe`
            : redirectUrl || appRoutes.orth;
          if (redirectedToPayment) {
            sessionStorage.removeItem('redirected-to-payment');
          }
          navigate(finalRedirectUrl, 'replace');
        },
        onError: stopLoading,
      }
    );
  };

  const onSubmit = (data: SignInFormSchema) => {
    const formEmail = form.getValues('email');

    if (data.passwordVisible) {
      handleLogin({
        ...data,
        email: formEmail,
      });
    } else handleCheckAuth(formEmail);
  };


  return (
    <>
      <div className='text-30 font-semibold text-center'>
        {passwordVisible ? t('login') : t('signUpLogin')}
        <div className='text-16 font-normal text-center'>
          {t('login_sub_header')}
        </div>
      </div>
      {!passwordVisible && (
        <SocialLoginContainer 
          isInvitation={isInvitation}
          accountId={id}
          email={form.getValues('email')}
        />
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-16'>
          <FormFieldLayout
            control={form.control}
            name='email'
            label={t('email')}
            disabled={!!email}
          >
            {({ field }) => (
              <InputField {...field} placeholder='john@aydi.com' />
            )}
          </FormFieldLayout>
          {passwordVisible && (
            <>
              <FormFieldLayout
                control={form.control}
                name='password'
                label={t('password')}
              >
                {({ field }) => <InputField.Password {...field} />}
              </FormFieldLayout>
              <Link
                className='text-12 font-medium text-primary flex justify-end'
                href={routes.forgotPassword}
              >
                {t('forgetPassword')}
              </Link>
            </>
          )}
          <Button
            disabled={
              passwordVisible
                ? isLoginLoading
                : isContinueLoading ||
                  isCheckAuthPending ||
                  isJoinAccountPending ||
                  isLoginPending
            }
            isLoading={
              passwordVisible
                ? isLoginLoading
                : isContinueLoading ||
                  isCheckAuthPending ||
                  isJoinAccountPending ||
                  isLoginPending
            }
            width='full'
            size='medium'
          >
            {t(`${passwordVisible ? 'login' : 'continue'}`)}
          </Button>
        </form>
      </Form>
    </>
  );
};

export default Login;
